import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { mergeSx } from '../utils/mergeSx';

export type ButtonIntent =
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'danger'
  | 'warning'
  | 'ghost';

/** `tiny` is 24px, `small` is 28px, `medium` is 32px, `large` is 40px. Heights come from the theme. */
export type ButtonSize = 'tiny' | 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<MuiButtonProps, 'size'> {
  intent?: ButtonIntent;
  size?: ButtonSize;
  /** React Router location when `component` is a link. */
  to?: string;
}

const intentStyles: Record<
  ButtonIntent,
  Pick<MuiButtonProps, 'variant' | 'color'>
> = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'outlined', color: 'primary' },
  neutral: { variant: 'outlined', color: 'inherit' },
  danger: { variant: 'contained', color: 'error' },
  warning: { variant: 'contained', color: 'warning' },
  ghost: { variant: 'text', color: 'primary' },
};

/** borderRadius from Theme (shape.borderRadius) — override via sx only for exceptions (e.g. radius.full pill). */
const Button = ({
  intent,
  variant,
  color,
  size = 'small',
  disableElevation = true,
  sx,
  ...props
}: ButtonProps) => {
  const fromIntent = intent ? intentStyles[intent] : undefined;

  return (
    <MuiButton
      variant={variant ?? fromIntent?.variant}
      color={color ?? fromIntent?.color}
      size={size}
      disableElevation={disableElevation}
      sx={mergeSx({ whiteSpace: 'nowrap', flexShrink: 0 }, sx)}
      {...props}
    />
  );
};

export default Button;
